import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { apiClient } from '../../../services/api/client';
import { locationApi } from '../../../services/api';

const getRoleDisplayName = (role) => {
    const map = {
        'User': 'User',
        'Manager': 'Manager',
 'TroncMaster': 'TRONC Master',
        'TenantAdmin': 'Administrator',
        'GlobalAdmin': 'Global Admin',
    };
  return map[role] || role;
};

const EditUserModal = ({ user, onClose, onSuccess, isGlobalAdmin, currentUserRole }) => {
    const [formData, setFormData] = useState({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        role: user.role || 'User',
        isActive: user.isActive ?? true,
    });
    const [selectedLocationIds, setSelectedLocationIds] = useState([]);
    const [locations, setLocations] = useState([]);
    const [userLocations, setUserLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [locationsLoading, setLocationsLoading] = useState(true);
    const [userLocationsLoading, setUserLocationsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [troncMasterExists, setTroncMasterExists] = useState(false);
    const [checkingTroncMaster, setCheckingTroncMaster] = useState(true);

    const showLocationField = formData.role === 'User' || formData.role === 'Manager' || formData.role === 'TroncMaster';
    const isAdminRole = formData.role === 'TenantAdmin' || formData.role === 'GlobalAdmin';

    // Build available role options based on current user's role
  const buildRoleOptions = () => {
 const roles = [
     { value: 'User', label: 'User', description: 'Basic access to view data' },
            { value: 'Manager', label: 'Manager', description: 'Can manage employees and distributions' },
     { value: 'TroncMaster', label: 'TRONC Master', description: 'Independent TRONC distribution manager (HMRC compliant)' },
          { value: 'TenantAdmin', label: 'Administrator', description: 'Full tenant administration access' },
        ];

   // Only GlobalAdmin can assign GlobalAdmin role — but requirement says you cannot upgrade a tenant user to GlobalAdmin
      // So we never show GlobalAdmin as an option for tenant-level users
      // GlobalAdmin can only be assigned during invitation (not via edit)

        // TenantAdmin can only assign roles up to TenantAdmin
        if (currentUserRole === 'TenantAdmin') {
   return roles; // Up to TenantAdmin
        }

        // GlobalAdmin can assign up to TenantAdmin for tenant users (cannot upgrade to GlobalAdmin)
        if (currentUserRole === 'GlobalAdmin') {
      return roles; // Still up to TenantAdmin for tenant-level users
        }

        return roles;
    };

    const roleOptions = buildRoleOptions();

    // Fetch locations dropdown
    useEffect(() => {
  const fetchLocations = async () => {
       try {
                setLocationsLoading(true);
            const response = await locationApi.getDropdown();
      const data = response?.data ?? response?.Data ?? [];
        const success = response?.success ?? response?.Success ?? false;
         if (success && data) {
         setLocations(data);
       }
    } catch (err) {
         console.error('Failed to fetch locations:', err);
            } finally {
     setLocationsLoading(false);
            }
        };
      fetchLocations();
    }, []);

    // Fetch user's current location access
    useEffect(() => {
        const fetchUserLocations = async () => {
            try {
          setUserLocationsLoading(true);
     const response = await apiClient.get(`/users/${user.id}/locations`);
      if (response.success && response.data) {
    setUserLocations(response.data);
        setSelectedLocationIds(response.data.map(l => l.locationId));
             }
   } catch (err) {
           console.error('Failed to fetch user locations:', err);
            } finally {
       setUserLocationsLoading(false);
            }
        };
        fetchUserLocations();
 }, [user.id]);

    // Check if a TroncMaster already exists (for blocking selection)
    useEffect(() => {
        const checkTroncMasterExists = async () => {
    try {
  setCheckingTroncMaster(true);
        const usersResponse = await apiClient.get('/users');
 const users = usersResponse?.data ?? usersResponse?.Data ?? [];
 // A TroncMaster exists if there's one active and it's NOT this user
      const hasTroncMasterUser = users.some(u => u.role === 'TroncMaster' && u.isActive && u.id !== user.id);
       if (hasTroncMasterUser) {
            setTroncMasterExists(true);
      } else {
const invitationsResponse = await apiClient.get('/users/invitations');
   const invitations = invitationsResponse?.data ?? invitationsResponse?.Data ?? [];
      const hasTroncMasterInvite = invitations.some(i => i.role === 'TroncMaster' && i.status === 'Pending');
         setTroncMasterExists(hasTroncMasterInvite);
        }
         } catch (err) {
    console.error('Failed to check TroncMaster status:', err);
    setTroncMasterExists(false);
        } finally {
       setCheckingTroncMaster(false);
    }
    };
        checkTroncMasterExists();
    }, [user.id]);

  const handleChange = (e) => {
 const { name, value, type, checked } = e.target;
        setFormData(prev => ({
   ...prev,
         [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const toggleLocation = (locationId) => {
        setSelectedLocationIds(prev =>
          prev.includes(locationId)
              ? prev.filter(id => id !== locationId)
         : [...prev, locationId]
        );
    };

    const handleSubmit = async (e) => {
    e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            // 1. Update user details and role
   const updatePayload = {
        firstName: formData.firstName,
                lastName: formData.lastName,
  role: formData.role,
    isActive: formData.isActive,
            };

 const updateResponse = await apiClient.put(`/users/${user.id}`, updatePayload);
            if (!updateResponse.success) {
            setError(updateResponse.message || 'Failed to update user');
        setLoading(false);
     return;
            }

          // 2. Update location access (only for non-admin roles)
          if (showLocationField && !isAdminRole) {
      try {
        await apiClient.put(`/users/${user.id}/locations`, {
          locationIds: selectedLocationIds,
   });
            } catch (locErr) {
               console.error('Failed to update location access:', locErr);
    // Don't block the whole save - user was updated successfully
 }
        }

    onSuccess();
 } catch (err) {
      setError(err.message || 'Failed to update user');
        } finally {
  setLoading(false);
        }
    };

    const isValid = formData.firstName && formData.lastName;
    const isTroncMasterBlocked = formData.role === 'TroncMaster' && troncMasterExists && user.role !== 'TroncMaster';
    const roleChanged = formData.role !== user.role;

    return (
 <div className='fixed inset-0 z-[1200] flex items-center justify-center'>
    <div className='absolute inset-0 bg-black/50' onClick={onClose} />
       <div className='relative bg-card rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto'>
            {/* Header */}
  <div className='flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card z-10'>
         <div>
             <h2 className='text-lg font-semibold'>Edit User</h2>
              <p className='text-sm text-muted-foreground'>{user.email}</p>
               </div>
       <button onClick={onClose} className='p-2 hover:bg-muted rounded-md'>
         <Icon name='X' size={20} />
       </button>
          </div>

       <form onSubmit={handleSubmit} className='p-4 space-y-5'>
    {/* Error */}
  {error && (
 <div className='alert alert-error'>
        <Icon name='AlertCircle' size={16} />
     <span>{error}</span>
           </div>
        )}

         {/* Name fields */}
      <div className='grid grid-cols-2 gap-4'>
             <div>
         <label className='block text-sm font-medium mb-1'>First Name *</label>
         <input
      type='text'
name='firstName'
         value={formData.firstName}
     onChange={handleChange}
           className='input w-full'
   required
        />
             </div>
      <div>
         <label className='block text-sm font-medium mb-1'>Last Name *</label>
                 <input
             type='text'
         name='lastName'
        value={formData.lastName}
       onChange={handleChange}
      className='input w-full'
               required
  />
   </div>
    </div>

    {/* Role */}
 <div>
       <label className='block text-sm font-medium mb-1'>Role *</label>
     <select
        name='role'
   value={formData.role}
  onChange={handleChange}
      className='select w-full'
             disabled={user.role === 'GlobalAdmin'}
             >
    {roleOptions.map(o => (
    <option
      key={o.value}
    value={o.value}
        disabled={o.value === 'TroncMaster' && troncMasterExists && user.role !== 'TroncMaster'}
        >
         {o.label}
     {o.value === 'TroncMaster' && troncMasterExists && user.role !== 'TroncMaster'
  ? ' (already assigned)'
  : ''}
    </option>
               ))}
            </select>

         {/* GlobalAdmin user cannot be changed */}
            {user.role === 'GlobalAdmin' && (
  <p className='text-xs text-muted-foreground mt-1'>
               <Icon name='Lock' size={12} className='inline mr-1' />
         Global Admin role cannot be changed from here.
           </p>
  )}

    {/* Role change warning */}
       {roleChanged && (
        <div className='mt-2 p-3 bg-warning/10 border border-warning/30 rounded-lg'>
     <p className='text-xs text-warning'>
      <Icon name='AlertTriangle' size={14} className='inline mr-1' />
        Changing role from <strong>{getRoleDisplayName(user.role)}</strong> to{' '}
                 <strong>{getRoleDisplayName(formData.role)}</strong>.
    This will affect the user's access permissions immediately.
          </p>
         </div>
         )}

        {/* TroncMaster info */}
         {formData.role === 'TroncMaster' && !troncMasterExists && (
        <div className='mt-2 p-3 bg-primary/5 border border-primary/20 rounded-lg'>
          <p className='text-xs text-muted-foreground'>
      <Icon name='Shield' size={14} className='inline mr-1 text-primary' />
  The TRONC Master must be independent of the employer and act without hiring authority or decision-making influence (HMRC requirement).
             </p>
        </div>
       )}

          {formData.role === 'TroncMaster' && troncMasterExists && user.role !== 'TroncMaster' && (
     <div className='mt-2 p-3 bg-warning/10 border border-warning/30 rounded-lg'>
             <p className='text-xs text-warning'>
     <Icon name='AlertTriangle' size={14} className='inline mr-1' />
            A TRONC Master already exists for this tenant. Only one TRONC Master is allowed.
     </p>
     </div>
    )}
     </div>

         {/* Active status */}
         <div className='flex items-center gap-3'>
             <label className='relative inline-flex items-center cursor-pointer'>
      <input
                  type='checkbox'
        name='isActive'
             checked={formData.isActive}
                onChange={handleChange}
   className='sr-only peer'
   />
      <div className="w-9 h-5 bg-muted rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
      </label>
  <span className='text-sm font-medium'>
           {formData.isActive ? 'Active' : 'Inactive'}
        </span>
      {!formData.isActive && user.isActive && (
         <span className='text-xs text-warning'>
     <Icon name='AlertTriangle' size={12} className='inline mr-1' />
        User will lose access
       </span>
             )}
  </div>

   {/* Location Access */}
      {showLocationField && !isAdminRole && (
   <div>
                  <label className='block text-sm font-medium mb-2'>
           <Icon name='MapPin' size={14} className='inline mr-1' />
           Location Access
    </label>
   <p className='text-xs text-muted-foreground mb-3'>
    Select which locations this user can access. Admins automatically have access to all locations.
            </p>

     {locationsLoading || userLocationsLoading ? (
            <div className='p-4 text-center text-sm text-muted-foreground'>
            <div className='spinner spinner-sm mx-auto mb-2' />
        Loading locations...
     </div>
      ) : locations.length === 0 ? (
    <div className='p-4 text-center text-sm text-muted-foreground border border-border rounded-lg'>
               No locations available.
 </div>
   ) : (
   <div className='border border-border rounded-lg divide-y divide-border max-h-48 overflow-y-auto'>
         {locations.map(loc => {
    const isSelected = selectedLocationIds.includes(loc.id);
            return (
              <label
        key={loc.id}
   className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50 transition-colors ${isSelected ? 'bg-primary/5' : ''}`}
        >
         <input
     type='checkbox'
        checked={isSelected}
        onChange={() => toggleLocation(loc.id)}
         className='rounded border-border text-primary focus:ring-primary'
       />
          <div className='flex-1 min-w-0'>
       <p className='text-sm font-medium truncate'>{loc.name}</p>
      {loc.code && (
     <p className='text-xs text-muted-foreground'>{loc.code}</p>
            )}
            </div>
       {isSelected && (
  <Icon name='Check' size={16} className='text-primary shrink-0' />
 )}
  </label>
              );
             })}
          </div>
             )}

     {selectedLocationIds.length > 0 && (
      <p className='text-xs text-muted-foreground mt-2'>
   {selectedLocationIds.length} location{selectedLocationIds.length !== 1 ? 's' : ''} selected
             </p>
        )}
      {selectedLocationIds.length === 0 && !locationsLoading && !userLocationsLoading && (
           <p className='text-xs text-warning mt-2'>
            <Icon name='AlertTriangle' size={12} className='inline mr-1' />
       No locations selected. The user won't have access to any location-specific data.
</p>
   )}
         </div>
)}

  {isAdminRole && (
       <div className='p-3 bg-muted/50 border border-border rounded-lg'>
         <p className='text-xs text-muted-foreground'>
      <Icon name='Info' size={14} className='inline mr-1 text-primary' />
             <strong>{getRoleDisplayName(formData.role)}</strong> users automatically have access to all locations within their tenant.
        </p>
         </div>
        )}

                    {/* Actions */}
             <div className='flex justify-end gap-3 pt-4 border-t border-border'>
            <button
       type='button'
                onClick={onClose}
                      className='btn btn-outline'
       disabled={loading}
         >
    Cancel
          </button>
        <button
  type='submit'
             className='btn btn-primary'
        disabled={loading || !isValid || isTroncMasterBlocked}
             >
         {loading ? (
        <>
        <div className='spinner spinner-sm' />
          Saving...
          </>
   ) : (
               <>
  <Icon name='Save' size={16} />
  Save Changes
</>
          )}
  </button>
        </div>
         </form>
        </div>
        </div>
    );
};

export default EditUserModal;
